import React from 'react'
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react'
import { Button } from './ui/button'
import { Card } from './ui/card'
import i18n from '../i18n/config'

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    }
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      errorId: Date.now().toString(36) + Math.random().toString(36).substr(2)
    }
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    })

    // Report error to monitoring service (if configured)
    if (window.reportError) {
      window.reportError(error, errorInfo)
    }
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    })
  }

  handleReload = () => {
    window.location.reload()
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      // Custom error UI
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="w-full max-w-md p-6">
            <div className="text-center space-y-4">
              <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              
              <div>
                <h1 className="text-xl font-semibold text-foreground">
                  {i18n.t('errors.boundary.title', 'Something went wrong')}
                </h1>
                <p className="text-sm text-muted-foreground mt-2">
                  {i18n.t('errors.boundary.message', "We encountered an unexpected error. This has been logged and we'll look into it.")}
                </p>
              </div>

              {/* Error ID for support */}
              {this.state.errorId && (
                <div className="bg-muted rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">
                    {i18n.t('errors.boundary.errorId', 'Error ID')}: <code className="font-mono">{this.state.errorId}</code>
                  </p>
                </div>
              )}

              {/* Development error details */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="text-left bg-red-50 border border-red-200 rounded-lg p-3">
                  <summary className="text-sm font-medium text-red-800 cursor-pointer">
                    {i18n.t('errors.boundary.errorDetails', 'Error Details (Development)')}
                  </summary>
                  <div className="mt-2 text-xs text-red-700 font-mono whitespace-pre-wrap">
                    {this.state.error.toString()}
                    {this.state.errorInfo.componentStack}
                  </div>
                </details>
              )}

              {/* Action buttons */}
              <div className="flex flex-col space-y-2">
                <Button onClick={this.handleRetry} className="w-full">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {i18n.t('errors.boundary.retryButton', 'Try Again')}
                </Button>
                
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    onClick={this.handleReload}
                    className="flex-1"
                  >
                    {i18n.t('errors.boundary.reloadButton', 'Reload Page')}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={this.handleGoHome}
                    className="flex-1"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    {i18n.t('errors.boundary.homeButton', 'Go Home')}
                  </Button>
                </div>

                {/* Report bug link */}
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    const subject = `Segnalazione errore - ${this.state.errorId}`
                    const body = `Codice errore: ${this.state.errorId}\nPagina: ${window.location.href}\nBrowser: ${navigator.userAgent}\n\nErrore: ${this.state.error?.toString()}`
                    window.open(`mailto:info@pingup.ai?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`)
                  }}
                  className="text-xs"
                >
                  <Bug className="w-3 h-3 mr-1" />
                  Segnala il problema
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )
    }

    return this.props.children
  }
}

// Hook version for functional components
export const useErrorHandler = () => {
  return (error, errorInfo) => {
    console.error('Error caught by error handler:', error, errorInfo)
    
    // Report to monitoring service
    if (window.reportError) {
      window.reportError(error, errorInfo)
    }
  }
}

export default ErrorBoundary